import * as React from 'react';
import { Users, BarChart3, Activity, Shield, KeyRound, Cpu, AudioLines, Palette, DollarSign, ImageIcon, Copy, RefreshCw, Bell, Flame } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { UsersTab } from '@/components/admin/users-tab';
import { UsageTab } from '@/components/admin/usage-tab';
import { UserStatsTab } from '@/components/admin/user-stats-tab';
import { PermissionsTab } from '@/components/admin/permissions-tab';
import { LlmConfigTab } from '@/components/admin/llm-config-tab';
import { SeedreamConfigTab } from '@/components/admin/seedream-config-tab';
import { AsrConfigTab } from '@/components/admin/asr-config-tab';
import { ThemeTab } from '@/components/admin/theme-tab';
import { TikhubConfigTab } from '@/components/admin/tikhub-config-tab';
import { CreditPricingTab } from '@/components/admin/credit-pricing-tab';
import { NotificationTemplatesTab } from '@/components/admin/notification-templates-tab';
import { HotspotAdminTab } from '@/components/admin/hotspot-admin-tab';
import { listInvitationCodes, generateInvitationCodes } from '@/api/client';

type InviteCode = {
  id: number;
  code: string;
  created_by_name: string;
  used_by_name: string | null;
  used_at: string | null;
  expires_at: string;
  created_at: string;
};

// 大 Tab 分组定义
const MAIN_TABS = [
  {
    key: 'system',
    label: '系统设置',
    icon: Cpu,
    subs: [
      { key: 'llm', label: 'LLM 配置', icon: Cpu },
      { key: 'asr', label: 'ASR 配置', icon: AudioLines },
      { key: 'seedream', label: '图像模型', icon: ImageIcon },
      { key: 'tikhub', label: 'TikHub 配置', icon: undefined as any },
    ],
  },
  {
    key: 'users',
    label: '用户管理',
    icon: Users,
    subs: [
      { key: 'codes', label: '邀请码', icon: KeyRound },
      { key: 'users', label: '用户管理', icon: Users },
      { key: 'stats', label: '用户数据', icon: Activity },
      { key: 'perms', label: '权限管理', icon: Shield },
      { key: 'notify', label: '通知模板', icon: Bell },
    ],
  },
  {
    key: 'ops',
    label: '运营与定价',
    icon: DollarSign,
    subs: [
      { key: 'credit', label: '积分定价', icon: DollarSign },
      { key: 'usage', label: '用量监控', icon: BarChart3 },
    ],
  },
  {
    key: 'ui',
    label: '界面与模板',
    icon: Palette,
    subs: [
      { key: 'theme', label: '主题设置', icon: Palette },
      { key: 'hotspots', label: '热点管理', icon: Flame },
    ],
  },
] as const;

type SubTabKey = 'llm' | 'asr' | 'seedream' | 'tikhub' | 'hotspots' | 'codes' | 'users' | 'stats' | 'perms' | 'notify' | 'credit' | 'usage' | 'theme';

export default function AdminPage() {
  const [mainTab, setMainTab] = React.useState('system');
  const [subTab, setSubTab] = React.useState<SubTabKey>('llm');

  // 切换大 Tab 时自动切换到该组的第一个子 Tab
  const handleMainTabChange = (key: string) => {
    setMainTab(key);
    const group = MAIN_TABS.find((g) => g.key === key);
    if (group && group.subs.length > 0) {
      setSubTab(group.subs[0].key as SubTabKey);
    }
  };

  const currentGroup = MAIN_TABS.find((g) => g.key === mainTab);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">后台管理</h1>
        <p className="mt-1 text-sm text-muted-foreground">系统服务、数据连接和模型平台配置</p>
      </div>

      {/* 大 Tab 导航 */}
      <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleMainTabChange(tab.key)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              mainTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* 次级 Tab 导航 */}
      {currentGroup && currentGroup.subs.length > 0 && (
        <div className="flex gap-1 rounded-lg border-b border-border pb-1">
          {currentGroup.subs.map((sub) => (
            <button
              key={sub.key}
              onClick={() => setSubTab(sub.key as SubTabKey)}
              className={`flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                subTab === sub.key
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {sub.icon && <sub.icon className="h-3.5 w-3.5" />}
              {sub.label}
            </button>
          ))}
        </div>
      )}

      {/* 子 Tab 内容 */}
      {subTab === 'codes' && <InvitationCodesSection />}
      {subTab === 'users' && <UsersTab />}
      {subTab === 'stats' && <UserStatsTab />}
      {subTab === 'perms' && <PermissionsTab />}
      {subTab === 'notify' && <NotificationTemplatesTab />}
      {subTab === 'usage' && <UsageTab />}
      {subTab === 'llm' && <LlmConfigTab />}
      {subTab === 'seedream' && <SeedreamConfigTab />}
      {subTab === 'asr' && <AsrConfigTab />}
      {subTab === 'tikhub' && <TikhubConfigTab />}
      {subTab === 'theme' && <ThemeTab />}
      {subTab === 'credit' && <CreditPricingTab />}
      {subTab === 'hotspots' && <HotspotAdminTab />}
    </div>
  );
}

function InvitationCodesSection() {
  const [codes, setCodes] = React.useState<InviteCode[]>([]);
  const [newCodes, setNewCodes] = React.useState<{ code: string; expiresAt: string }[]>([]);
  const [count, setCount] = React.useState(3);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listInvitationCodes();
      setCodes(data.codes);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleGenerate = React.useCallback(async () => {
    setGenerating(true);
    setNewCodes([]);
    try {
      const data = await generateInvitationCodes(count);
      setNewCodes(data.codes);
      await load();
    } catch (err) {
      console.error('生成失败', err);
    } finally {
      setGenerating(false);
    }
  }, [count, load]);

  const handleCopy = React.useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  }, []);

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>邀请码管理</CardTitle>
          <CardDescription>生成邀请码供新用户注册使用。邀请码 24 小时有效，使用后自动失效。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">生成数量</label>
              <Input
                type="number" min={1} max={50}
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                className="w-24"
              />
            </div>
            <Button onClick={handleGenerate} disabled={generating}>
              <KeyRound className="h-4 w-4" />
              {generating ? '生成中…' : '生成邀请码'}
            </Button>
          </div>
          {newCodes.length > 0 && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="mb-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                新生成的邀请码（24 小时有效，尽快分发给用户）
              </div>
              <div className="flex flex-wrap gap-2">
                {newCodes.map((item) => (
                  <div key={item.code} className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-mono">
                    <span className="font-bold text-foreground">{item.code}</span>
                    <button onClick={() => handleCopy(item.code)} className="text-muted-foreground hover:text-foreground" title="复制邀请码">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    {copiedCode === item.code && <span className="text-xs text-emerald-500">已复制</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>邀请码历史</CardTitle>
              <CardDescription>最近 200 条邀请码记录</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={load} title="刷新">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">邀请码</th>
                  <th className="px-4 py-3 text-left font-medium">创建人</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-left font-medium">使用人</th>
                  <th className="px-4 py-3 text-right font-medium">过期时间</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-muted-foreground" colSpan={5}>加载中…</td>
                  </tr>
                ) : codes.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-muted-foreground" colSpan={5}>暂无邀请码记录</td>
                  </tr>
                ) : (
                  codes.map((code) => {
                    const expired = !code.used_by_name && isExpired(code.expires_at);
                    return (
                      <tr key={code.id} className="border-b last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{code.code}</td>
                        <td className="px-4 py-3">{code.created_by_name || '—'}</td>
                        <td className="px-4 py-3">
                          {code.used_by_name ? <Badge variant="success">已使用</Badge>
                            : expired ? <Badge variant="destructive">已过期</Badge>
                            : <Badge variant="default">有效</Badge>}
                        </td>
                        <td className="px-4 py-3">{code.used_by_name || '—'}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          {code.expires_at ? new Date(code.expires_at).toLocaleString('zh-CN') : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}